import { useState, useEffect } from "react";
import {
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    Typography,
    Autocomplete, // For displaying success messages
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import LoadingProgressBar from "../../../components/loading-progressbar/loadingprogressbar";
import Footer from "../../../layouts/main/Footer";
import LoyaltySetOpeningPointService from "./LoyaltySetOpeningPointService";
import LoyaltyHeader from "./LoyaltyHeader";
import LoyaltyPopup from "../../../commons/PopUpHandle"

const LoyaltySetOpeningPoint = () => {
    const [loyaltyControls, setLoyaltyControls] = useState([]);
    const [selectedYear, setSelectedYear] = useState(null);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [error, setError] = useState(null);
    const [errorCode, setErrorCode] = useState(null);
    const [showSetOpening, setShowSetOpening] = useState(false); // For Set Opening button
    const [successMessage, setSuccessMessage] = useState(""); // For storing success messages

    const handleClose = () => setOpen(false);



    useEffect(() => {
        setLoading(true);
        LoyaltySetOpeningPointService.then((LoyaltySetOpeningPointServiceInstance) => {
            LoyaltySetOpeningPointServiceInstance.GetCreateLoyaltyOpeningPointCalControls()
                .then((response) => {
                    if (response.status === 200) {
                        setLoyaltyControls(response.data);
                    } else {
                         //  console.log("Error fetching controls");
                    }
                })
                .catch((err) => {
                     //  console.log("API Error:", err);
                })
                .finally(() => setLoading(false));
        });
    }, []);

    const handleValidate = () => { // NOSONAR
        if (!selectedYear) return; // Prevent validation if no value is selected

        const payload = {
            Id: selectedYear.Id,
            Year: selectedYear.Year,
            OpeningType: 1,
        };

        setLoading(true);
        LoyaltySetOpeningPointService.then((LoyaltySetOpeningPointServiceInstance) => {
            LoyaltySetOpeningPointServiceInstance.SetLoyaltyOpening(payload)
                .then((response) => {
                    const message = response?.data?.Message || "";
                    const isSuccess = response?.data?.IsSuccess;
                    if (response.status === 200) {
                         //  console.log("Validate API Response:", response.data);                       
                        if (!isSuccess) {
                            if (message.toLowerCase().includes("does not found")) {
                                setErrorCode(400); // Validation warning
                            } else {
                                setErrorCode(500); // General error
                            }
                            setError(message);
                            setOpen(true);
                        } else {
                            setSuccessMessage(message);
                            setShowSetOpening(true);
                        }
                    } else {
                         //  console.log("Error in Validate response");
                    }
                })
                .catch((err) => {
                     //  console.log("API Error:", err);
                    setErrorCode(err?.response?.status)
                    setError(err?.response?.data?.error?.message);
                    setOpen(true);
                })
                .finally(() => setLoading(false));
        });
    };

    const handleSetOpening = () => { // NOSONAR
        if (!selectedYear) return; 
        if (!showSetOpening) return;

        const payload = {
            Id: selectedYear.Id,
            Year: selectedYear.Year,
            OpeningType: 3,
        };

        setLoading(true);
        LoyaltySetOpeningPointService.then((LoyaltySetOpeningPointServiceInstance) => {
            LoyaltySetOpeningPointServiceInstance.SetLoyaltyOpening(payload)
                .then((response) => {
                    const message = response?.data?.Message || "";
                    const isSuccess = response?.data?.IsSuccess;
                    if (response.status === 200) {
                         //  console.log("Set Opening API Response:", response.data);                       
                        if (!isSuccess) {
                            if (message.toLowerCase().includes("does not found")) {
                                setErrorCode(400); // Validation warning
                            } else {
                                setErrorCode(500); // General error
                            }
                            setError(message);
                            setOpen(true);
                        } else {
                            setSuccessMessage(message);
                            setShowSetOpening(true);
                        }
                    } else {
                         //  console.log("Error in Set Opening response");
                    }
                })
                .catch((err) => {
                     //  console.log("API Error:", err);
                    setErrorCode(err?.response?.status)
                    setError(err?.response?.data?.error?.message);
                    setOpen(true);
                })
                .finally(() => setLoading(false));
        });
    };

    return (
        <>
            <Card
                sx={{
                    padding: "2px",
                    boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
                    borderRadius: "10px",
                    margin: "5px 3px 5px 3px",
                    height: "100%",
                    boxSizing: "border-box",
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                }}
            >
                <CardContent
                    style={{
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: "column",
                        boxSizing: "border-box",
                        paddingBottom: "0.5rem",
                        minHeight: 0,
                    }}
                >
                    <Helmet>
                        <title> Loyalty Set Opening | ProClaimz </title>
                    </Helmet>
                    <LoyaltyHeader />
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: '1fr 1fr',
                            md: '1fr 1fr',
                            lg: '1fr 1fr 1fr',
                        },
                        marginTop: '1rem',
                        paddingBottom: '0.5rem',
                        gap: '1rem',
                    }}>
                    {loyaltyControls.map((control, index) => {
                        if (control.DataType === "Dropdown") {
                            return (
                                <Autocomplete
                                    key={index} // NOSONAR
                                    options={control.DefaultValues}
                                    getOptionLabel={(option) => {
                                        const label = option[control.DisplayExpr];
                                        return label ? String(label) : ""; // Ensure the value is returned as a string
                                    }}
                                    onChange={(event, newValue, reason) => {
                                        if (!(reason === 'removeOption' && event.type === 'keydown')) {
                                        setSelectedYear(newValue);
                                        setSuccessMessage(""); // Clear the success message when dropdown value changes
                                        setShowSetOpening(false); // Hide the Set Opening button when dropdown value changes
                                        setError(null);               
                                        setErrorCode(null);         
                                        setOpen(false);               
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={control.LabelName}
                                            variant="outlined"                                            
                                            size='small'
                                            sx={{
                                                width: '100%',
                                                '& .MuiInputBase-root': {
                                                    padding: '0 12px',
                                                    fontSize: '13px',
                                                },
                                                '& .MuiInputBase-input': {
                                                    padding: '8.5px',
                                                    fontSize: '13px',
                                                }, '& .MuiFormLabel-root': {
                                                    fontSize: '14px',
                                                }
                                            }}
                                        />
                                    )}
                                    value={selectedYear}
                                />
                            );
                        }
                        return null;
                    })}
                        <Box sx={{ display: "flex", gap: "0.5em", alignItems: "center", }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleValidate}
                                sx={{
                                    padding:'0px 4px',
                                    minWidth: "auto", // Ensures the button doesn't take up extra space
                                    height: "30px", // Sets a fixed height for uniformity
                                    fontSize: "13px",
                                    lineHeight: "normal", // Adjusts text alignment without extra padding
                                }}
                            >
                                Validate
                            </Button>

                            {showSetOpening && (
                                <Button
                                    variant="contained"
                                    onClick={handleSetOpening}
                                    sx={{
                                        backgroundColor: "#43b5a2",
                                        "&:hover": { backgroundColor: "#369a88" },
                                        padding: '0px 4px',
                                        minWidth: "auto",
                                        height: "30px",
                                        fontSize: "13px",
                                        lineHeight: "normal",
                                        color: "white",
                                    }}
                                >
                                    Set Opening
                                </Button>
                            )}
                        </Box>                   
                    </Box>
                    {successMessage && (
                        <Typography
                            variant="body2"
                            color="green"                          
                        >
                            {successMessage}
                        </Typography>
                    )}
                    {open && (
                        <LoyaltyPopup
                            open={open}
                            handleClose={handleClose}
                            error={error}
                            errorCode={errorCode}
                        />
                    )}
                </CardContent>
            </Card>
            {loading && <LoadingProgressBar />}
            <Footer />
        </>
    );
};

export default LoyaltySetOpeningPoint;
